import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useHistory } from 'react-router-dom';
import Select from 'react-select';
import {
    Button, Card, CardBody, Col,
    Form, FormGroup, Label,
    Modal,
    ModalBody,
    ModalHeader,
    Row
} from 'reactstrap';
import { InputField } from 'src/components';
import { setFromErrors } from 'src/helpers';
import { useItmcTeamCreateMutation } from 'src/rtk/features/instituteInspection/institutueInspectionApi';
import { useGetOrganizationsQuery } from 'src/rtk/features/setup/organization/setupOrganizationApi';
import { useGetSupervisorsByInstituteQuery } from 'src/rtk/features/trainingRecord/trainingRecordlApi';
import * as Yup from 'yup';

interface Props {
    isOpen: boolean;
    toggle: any;
    rowInfo: any;
}

const initialValues = {
    itmc_team_id: '',
    committee_name: '',
    institute_id: '',
    department_id: '',
    chairman_id: '',
    members: [],
};

const UpdateItmcModal = (props: Props) => {
    const { isOpen, toggle, rowInfo } = props;
    const [itmcTeamCreate, { isSuccess, isError, error }] = useItmcTeamCreateMutation();
    const [btnClicked, setBtnClicked] = useState<string>('');
    const [selectedChairman, setSelectedChairman] = useState<any>(null);
    const [selectedMembers, setSelectedMembers] = useState<any[]>([]);
    const [selectedSecretaries, setSelectedSecretaries] = useState<any>(null);
    const [selectedInstitute, setSelectedInstitute] = useState<any>(null);
    const [isDataLoaded, setIsDataLoaded] = useState(false);
    const history = useHistory();

    const { data: instituteList } = useGetOrganizationsQuery();
    const { data: supervisors } = useGetSupervisorsByInstituteQuery(
        selectedInstitute ? selectedInstitute.value : ''
    );

    const allInstitute: SelectOption[] =
        instituteList?.data?.map((item) => ({
            value: item.id!,
            label: item.organization_name,
        })) || [];

    const allChairmanMember: SelectOption[] =
        supervisors?.data?.map((item) => ({
            value: item.user_id!,
            label: item.full_name!,
        })) || [];

    const err: CustomError = error as CustomError;

    const {
        handleSubmit,
        getFieldProps,
        handleChange,
        handleBlur,
        values,
        touched,
        errors,
        resetForm,
        setFieldValue,
        setErrors,
        setValues,
    } = useFormik({
        initialValues,

        validationSchema: Yup.object({
            institute_id: Yup.string().required('Select Institute'),
            committee_name: Yup.string().required('Team Name is required'),
            chairman_id: Yup.string().required('Select Chairman'),
        }),

        onSubmit: (values: any) => {
            console.log(values);
            itmcTeamCreate(values);
        },
    });

    // Reset data loaded flag when modal opens with new rowInfo
    useEffect(() => {
        if (isOpen && rowInfo?.id) {
            setIsDataLoaded(false);
        }
    }, [isOpen, rowInfo?.id]);

    useEffect(() => {
        if (isSuccess) {
            resetForm();
            setSelectedChairman(null);
            setSelectedMembers([]);
            setSelectedSecretaries(null);
            setSelectedInstitute(null);
            setIsDataLoaded(false);
            toast.success('Team Updated Successfully!');
            toggle();
        }
        if (isError) {
            toast.error(err?.message || 'Something Went Wrong!');
            setFromErrors(err.data, setErrors);
        }
    }, [isSuccess, isError]);

    // Handle member selection (member_type: 'member')
    const handleMember = (selectedOptions: any) => {
        const options = selectedOptions || [];
        setSelectedMembers(options);
        updateMembersArray(options, selectedSecretaries);
    };

    // Handle secretary selection (member_type: 'secretary')
    const handleSecretary = (selectedOption: any) => {
        setSelectedSecretaries(selectedOption);
        updateMembersArray(selectedMembers, selectedOption);
    };

    // Update the members array with both members and secretaries
    const updateMembersArray = (members: any[], secretary: any) => {
        const membersArray: any[] = [];

        // Add members with member_type: 'member'
        members.forEach((member) => {
            membersArray.push({
                member_id: member.value,
                member_type: 'member'
            });
        });

        // Add secretary with member_type: 'secretary'
        if (secretary) {
            membersArray.push({
                member_id: secretary.value,
                member_type: 'secretary'
            });
        }

        setFieldValue('members', membersArray);
    };

    // Handle chairman change
    const handleChairmanChange = (selectedOption: any) => {
        setSelectedChairman(selectedOption);
        if (selectedOption) {
            setFieldValue('chairman_id', selectedOption.value);
        } else {
            setFieldValue('chairman_id', '');
        }
    };

    // Handle institute change
    const handleInstituteChange = (selectedOption: any) => {
        setSelectedInstitute(selectedOption);
        if (selectedOption) {
            setFieldValue('institute_id', selectedOption.value);
        } else {
            setFieldValue('institute_id', '');
        }
        // Reset chairman and members when institute changes
        setSelectedChairman(null);
        setSelectedMembers([]);
        setSelectedSecretaries(null);
        setFieldValue('chairman_id', '');
        setFieldValue('members', []);
    };

    // Get selected member IDs to filter from available options
    const getSelectedMemberIds = () => {
        const selectedIds = [];
        if (selectedChairman) selectedIds.push(selectedChairman.value);
        selectedMembers.forEach(member => selectedIds.push(member.value));
        if (selectedSecretaries) selectedIds.push(selectedSecretaries.value);
        return selectedIds;
    };

    // Filter available options excluding chairman and already selected members/secretaries
    const getAvailableOptions = () => {
        const selectedIds = getSelectedMemberIds();
        return allChairmanMember.filter((option: any) => !selectedIds.includes(option.value));
    };
    // Populate form when rowInfo changes - SINGLE EFFECT WITH FLAG
    useEffect(() => {
        if (!isOpen || !rowInfo?.id || isDataLoaded) {
            return;
        }

        // Wait for institute list to be loaded
        if (allInstitute.length === 0) {
            return;
        }

        // Set institute first
        const instituteOption = allInstitute.find(option => option.value === rowInfo.institute_id);
        if (!instituteOption) {
            return;
        }

        // Set institute selection
        setSelectedInstitute(instituteOption);

        const membersArray: [] = rowInfo?.itmc_team_members?.map((member: any) => ({
            member_id: parseInt(member.member_id),
            member_type: member.member_type
        })) || [];

        // Set basic form values
        setValues({
            itmc_team_id: rowInfo?.id || '',
            committee_name: rowInfo?.committee_name || '',
            institute_id: rowInfo?.institute_id || '',
            department_id: rowInfo?.department_id || '',
            chairman_id: rowInfo?.chairman_id || '',
            members: membersArray || [],
        });

        // Wait for supervisors to load before setting chairman and members
        if (allChairmanMember.length > 0) {
            // Separate members by type
            const members = rowInfo?.itmc_team_members?.filter((member: any) => member?.member_type === 'member') || [];
            const secretaries = rowInfo?.itmc_team_members?.filter((member: any) => member?.member_type === 'secretary') || [];

            // Set chairman
            const chairmanOption = allChairmanMember.find(option => option.value.toString() === rowInfo.chairman_id);
            setSelectedChairman(chairmanOption || '');

            // Set members
            const selectedMemberOptions = members.map((member: any) =>
                allChairmanMember.find(option => option.value.toString() === member.member_id)
            ).filter(Boolean);
            setSelectedMembers(selectedMemberOptions);

            // Set secretary (single select)
            const selectedSecretaryOption = secretaries.length > 0
                ? allChairmanMember.find(option => option.value.toString() === secretaries[0].member_id)
                : null;
            setSelectedSecretaries(selectedSecretaryOption || null);

            // Mark as loaded to prevent re-running
            setIsDataLoaded(true);
        }
    }, [isOpen, rowInfo?.id, allInstitute.length, allChairmanMember.length, isDataLoaded]);

    return (
        <Modal isOpen={isOpen} toggle={toggle} backdrop={'static'} size="lg">
            <ModalHeader toggle={toggle}>Update ITMC Team Details</ModalHeader>

            <ModalBody>
                <Card>
                    <CardBody>
                        <Form
                            onSubmit={(e) => {
                                e.preventDefault();
                                handleSubmit();
                            }}
                            onReset={(e) => {
                                resetForm();
                                setSelectedChairman(null);
                                setSelectedMembers([]);
                                setSelectedSecretaries(null);
                                setSelectedInstitute(null);
                            }}
                        >
                            <Row>
                                <Col lg={6} md={6} sm={12}>
                                    <FormGroup>
                                        <Label for="committee_name" className="form-label required-field">Team Name</Label>
                                        <InputField
                                            type="text"
                                            name='committee_name'
                                            placeholder='Team Name'
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            value={values.committee_name}
                                            invalid={{ errors, touched }}
                                        />
                                    </FormGroup>
                                </Col>

                                <Col lg={6} md={6} sm={12}>
                                    <FormGroup>
                                        <Label for="institute_id" className="form-label required-field">Institute</Label>
                                        <Select
                                            isClearable={true}
                                            isSearchable={true}
                                            value={selectedInstitute}
                                            onChange={handleInstituteChange}
                                            options={allInstitute}
                                            placeholder='Select Institute'
                                            name="institute_id"
                                        />
                                        {errors.institute_id && touched.institute_id ? (
                                            <div className="text-danger">{errors.institute_id as string}</div>
                                        ) : null}
                                    </FormGroup>
                                </Col>

                                <Col lg={6} md={6} sm={12}>
                                    <FormGroup>
                                        <Label for="chairman_id" className="form-label required-field">Chairman</Label>
                                        <Select
                                            isClearable={true}
                                            isSearchable={true}
                                            value={selectedChairman}
                                            onChange={handleChairmanChange}
                                            options={allChairmanMember}
                                            placeholder='Select Chairman'
                                            name="chairman_id"
                                        />
                                        {errors.chairman_id && touched.chairman_id ? (
                                            <div className="text-danger">{errors.chairman_id as string}</div>
                                        ) : null}
                                    </FormGroup>
                                </Col>

                                <Col lg={6} md={6} sm={12}>
                                    <FormGroup>
                                        <Label for="members">Members</Label>
                                        <Select
                                            isClearable={true}
                                            isSearchable={true}
                                            isMulti={true}
                                            value={selectedMembers}
                                            onChange={handleMember}
                                            options={getAvailableOptions()}
                                            placeholder='Select Members'
                                            name="members"
                                        />
                                    </FormGroup>
                                </Col>

                                <Col lg={6} md={6} sm={12}>
                                    <FormGroup>
                                        <Label for="secretary" className="form-label">Members Secretary</Label>
                                        <Select
                                            isClearable={true}
                                            isSearchable={true}
                                            value={selectedSecretaries}
                                            onChange={handleSecretary}
                                            options={getAvailableOptions()}
                                            placeholder='Select Members Secretary'
                                            name="secretary"
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <FormGroup className="mb-3 text-center">
                                    <Button
                                        type="reset"
                                        className="btn btn-secondary me-2"
                                    >
                                        Reset
                                    </Button>

                                    <button
                                        type="submit"
                                        className="btn btn-success"
                                        onClick={(e) => {
                                            setBtnClicked('submit');
                                        }}
                                    >
                                        Submit
                                    </button>
                                </FormGroup>
                            </Row>
                        </Form>
                    </CardBody>
                </Card>
            </ModalBody>

        </Modal>
    );
};

export default UpdateItmcModal;